import { useEffect, useState } from 'react';
import { getCustomer } from '../../../requests';
import { CustomerInfo, EntryValueDetails } from '../../../types';
import { MiraFieldProps } from './ComponentTypes';
import { useLookupRequestState } from './useLookupRequestState';
import { useMiraLookupFieldSync } from './useMiraLookupFieldSync';
import { LookupIdField } from './LookupIdField';
import { runLookupRequest } from './runLookupRequest';

const CustomerId: React.FC<MiraFieldProps> = ({
  label,
  onChange,
  errors,
  value,
  prop,
  setValue,
  formData
}) => {
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({});
  const miraSearchEnabled = prop.props?.miraSearchEnabled ?? false;
  const {
    isLoading,
    notFound: customerNotFound,
    requestError: customerGetError,
    beginRequest,
    markNotFound,
    markRequestError,
    endRequest
  } = useLookupRequestState();

  const { clearLookupFields, setLookupFieldsFromResponse } =
    useMiraLookupFieldSync({
      formData,
      setValue,
      source: 'customer'
    });

  const clearValues = () => {
    setCustomerName('');
    setCustomerInfo({});
    clearLookupFields();
  };

  useEffect(() => {
    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const fetchCustomerData = () => {
    const setValueDetails = (response: CustomerInfo) => {
      const customerName = response?.OrganizationName;
      const valueDetails: EntryValueDetails = {
        ...(customerName !== undefined && customerName !== null
          ? {
              customerName
            }
          : {}),
        ...(response.LRTRentalPriceGroupId !== undefined &&
        response.LRTRentalPriceGroupId !== null
          ? { rentalPriceGroupId: response.LRTRentalPriceGroupId }
          : {}),
        ...(response.CustClassificationId !== undefined &&
        response.CustClassificationId !== null
          ? { customerClassificationId: response.CustClassificationId }
          : {})
      };

      setValue(
        prop.id.toString(),
        {
          value: customerId,
          ...(Object.keys(valueDetails).length > 0 ? { valueDetails } : {})
        },
        {
          shouldValidate: true
        }
      );
    };

    runLookupRequest({
      isEnabled: Boolean(customerId && miraSearchEnabled),
      clearValues,
      beginRequest,
      endRequest,
      markNotFound,
      markRequestError,
      fetchLookup: () => getCustomer(customerId),
      onSuccess: (response) => {
        const resolvedCustomerName =
          response?.OrganizationName ??
          `Asiakkaalle ${customerId} ei ole asetettu nimeä`;

        setCustomerName(resolvedCustomerName);
        setCustomerInfo(response);
        setLookupFieldsFromResponse(response);
        setValueDetails(response);
      }
    });
  };

  return (
    <LookupIdField
      label={label}
      value={value}
      onChange={onChange}
      errors={errors}
      prop={prop}
      lookupId={customerId}
      setLookupId={setCustomerId}
      isLoading={isLoading}
      notFound={customerNotFound}
      requestError={customerGetError}
      notFoundMessage={`Asiakasnumerolla ${customerId} ei löytynyt asiakasta Mirasta.`}
      requestErrorMessage={
        'Asiakkaan hakemisessa tapahtui virhe. Asiakastiedot joudutaan syöttämään käsin.'
      }
      lookupDetails={[
        {
          key: 'customer-name',
          label: 'Mira-nimi:',
          value: customerName
        },
        {
          key: 'lrt-rental-price-group-id',
          label: 'Mira-vuokrahintaryhma:',
          value: customerInfo.LRTRentalPriceGroupId ?? ''
        },
        {
          key: 'cust-classification-id',
          label: 'Mira-asiakasluokitus:',
          value: customerInfo.CustClassificationId ?? ''
        }
      ]}
      onClearValues={clearValues}
    />
  );
};

export { CustomerId };
